import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import api from "../api";

const VanFeeDetailedReport = () => {
  const [reportPayload, setReportPayload] = useState({
    meta: null,
    data: [],
  });
  const [school, setSchool] = useState(null);
  const [sessions, setSessions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedClass, setSelectedClass] = useState("all");

  const normalizeSessions = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.sessions)) return payload.sessions;
    return [];
  };

  const getDefaultSessionId = (sessionRows = []) => {
    if (!sessionRows.length) return "";

    const activeSession =
      sessionRows.find(
        (s) =>
          s?.is_active === true ||
          s?.is_active === 1 ||
          s?.isActive === true ||
          s?.status === "active"
      ) || null;

    if (activeSession?.id) return String(activeSession.id);

    const sorted = [...sessionRows].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
    return sorted[0]?.id ? String(sorted[0].id) : "";
  };

  const fetchSessions = async () => {
    const res = await api.get("/sessions");
    return normalizeSessions(res.data);
  };

  const fetchSchool = async () => {
    const res = await api.get("/schools");
    return Array.isArray(res.data) && res.data.length > 0 ? res.data[0] : null;
  };

  const fetchReport = async (sessionId) => {
    const res = await api.get("/feedue/van-fee-detailed-report", {
      params: { session_id: sessionId },
    });

    if (Array.isArray(res.data)) {
      return {
        meta: null,
        data: res.data,
      };
    }

    return {
      meta: res.data?.meta || null,
      data: Array.isArray(res.data?.data) ? res.data.data : [],
    };
  };

  const loadInitialData = async () => {
    setLoading(true);
    setError("");

    try {
      const [sessionRows, schoolData] = await Promise.all([
        fetchSessions(),
        fetchSchool(),
      ]);

      setSessions(sessionRows);
      setSchool(schoolData);

      const defaultSessionId = getDefaultSessionId(sessionRows);
      setSelectedSession(defaultSessionId);
    } catch (err) {
      console.error("Error loading initial van fee report data:", err);
      setError("Failed to load sessions or school data.");
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (sessionId, mode = "load") => {
    if (!sessionId) {
      setReportPayload({ meta: null, data: [] });
      return;
    }

    if (mode === "refresh") {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const payload = await fetchReport(sessionId);
      setReportPayload(payload);
    } catch (err) {
      console.error("Error loading van fee detailed report:", err);
      setError("Failed to load van fee detailed report.");
      setReportPayload({ meta: null, data: [] });
    } finally {
      if (mode === "refresh") {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      loadReport(selectedSession);
    }
  }, [selectedSession]);

  const report = reportPayload?.data || [];

  const classOptions = useMemo(() => {
    return report
      .map((item) => ({
        classId: item?.classId ?? "",
        className: item?.className || "Unknown Class",
      }))
      .filter((item) => item.className)
      .sort((a, b) => String(a.className).localeCompare(String(b.className)));
  }, [report]);

  const filteredReport = useMemo(() => {
    if (selectedClass === "all") return report;
    return report.filter((item) => String(item?.classId) === String(selectedClass));
  }, [report, selectedClass]);

  const selectedSessionName = useMemo(() => {
    const row = sessions.find((s) => String(s?.id) === String(selectedSession));
    return row?.name || reportPayload?.meta?.sessionName || "N/A";
  }, [sessions, selectedSession, reportPayload]);

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    return num.toLocaleString("en-IN");
  };

  const summary = useMemo(() => {
    let totalStudents = 0;
    let totalEntries = 0;
    let totalAmount = 0;

    filteredReport.forEach((cls) => {
      const students = Array.isArray(cls?.students) ? cls.students : [];
      totalStudents += students.length;

      students.forEach((stu) => {
        const fees = stu?.fees || {};
        totalEntries += Object.keys(fees).length;
        totalAmount += Number(stu?.totalVanFeePaid || 0);
      });
    });

    return {
      totalClasses: filteredReport.length,
      totalStudents,
      totalEntries,
      totalAmount,
    };
  }, [filteredReport]);

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExcelDownload = async () => {
    if (!selectedSession) {
      setError("Please select a session first.");
      return;
    }

    try {
      setExportingExcel(true);
      setError("");

      const params = { session_id: selectedSession };
      if (selectedClass !== "all") {
        params.class_id = selectedClass;
      }

      const res = await api.get("/feedue/van-fee-detailed-report/excel", {
        params,
        responseType: "blob",
      });

      const fileName = `Van_Fee_Detailed_Report_${selectedSessionName}.xlsx`;
      downloadBlob(res.data, fileName);
    } catch (err) {
      console.error("Excel download error:", err);
      setError("Failed to download Excel report.");
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePdfDownload = async () => {
    if (!selectedSession) {
      setError("Please select a session first.");
      return;
    }

    try {
      setExportingPdf(true);
      setError("");

      const params = { session_id: selectedSession };
      if (selectedClass !== "all") {
        params.class_id = selectedClass;
      }

      const res = await api.get("/feedue/van-fee-detailed-report/pdf", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("PDF download error:", err);
      setError("Failed to generate PDF report.");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Container fluid className="py-4 px-3 px-md-4">
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <Row className="align-items-center g-3">
            <Col md={7}>
              <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                <h3 className="mb-0 fw-bold text-dark">Van Fee Detailed Report</h3>
                <Badge bg="primary" pill>
                  Session Wise
                </Badge>
              </div>
              <div className="text-muted">
                View class-wise van fee data in monthly pivot format with session-wise filtering.
              </div>
            </Col>

            <Col md={5}>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end">
                  <Form.Select
                    value={selectedSession}
                    onChange={(e) => {
                      setSelectedSession(e.target.value);
                      setSelectedClass("all");
                    }}
                    style={{ maxWidth: "220px" }}
                  >
                    <option value="">Select Session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name || `Session ${session.id}`}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    style={{ maxWidth: "220px" }}
                    disabled={!filteredReport && !report.length}
                  >
                    <option value="all">All Classes</option>
                    {classOptions.map((cls) => (
                      <option key={`${cls.classId}-${cls.className}`} value={cls.classId}>
                        {cls.className}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => loadReport(selectedSession, "refresh")}
                    disabled={refreshing || !selectedSession}
                  >
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </Button>

                  <Button
                    variant="success"
                    onClick={handleExcelDownload}
                    disabled={exportingExcel || !selectedSession || filteredReport.length === 0}
                  >
                    {exportingExcel ? "Downloading Excel..." : "Download Excel"}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handlePdfDownload}
                    disabled={exportingPdf || !selectedSession || filteredReport.length === 0}
                  >
                    {exportingPdf ? "Generating PDF..." : "Download PDF"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="shadow-sm">
          {error}
        </Alert>
      )}

      {loading ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <div className="fw-semibold">Loading report...</div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small mb-1">Session</div>
                  <h6 className="mb-0 fw-bold">{selectedSessionName}</h6>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small mb-1">Classes</div>
                  <h4 className="mb-0 fw-bold">{summary.totalClasses}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small mb-1">Students</div>
                  <h4 className="mb-0 fw-bold">{summary.totalStudents}</h4>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small mb-1">Total Amount</div>
                  <h4 className="mb-0 fw-bold">{formatAmount(summary.totalAmount)}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="text-muted small mb-1">Van Fee Entries</div>
                  <h4 className="mb-0 fw-bold">{summary.totalEntries}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {filteredReport.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5 text-muted">
                No report data found for the selected session/class.
              </Card.Body>
            </Card>
          ) : (
            filteredReport.map((cls, idx) => {
              const months = Array.isArray(cls?.months) ? cls.months : [];
              const students = Array.isArray(cls?.students) ? cls.students : [];

              return (
                <Card key={`${cls.classId || cls.className}-${idx}`} className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white border-0 pt-4 px-4 pb-2">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                      <div>
                        <h5 className="mb-1 fw-bold text-primary">{cls.className || "Unknown Class"}</h5>
                        <div className="text-muted small">
                          Total Students: {students.length}
                        </div>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Body className="px-4 pb-4 pt-2">
                    <div className="table-responsive">
                      <Table bordered hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ minWidth: "70px" }}>#</th>
                            <th style={{ minWidth: "150px" }}>Admission No</th>
                            <th style={{ minWidth: "260px" }}>Student Name</th>
                            {months.map((month, i) => (
                              <th key={i} style={{ minWidth: "140px" }}>
                                {month}
                              </th>
                            ))}
                            <th style={{ minWidth: "140px" }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.length > 0 ? (
                            <>
                              {students.map((stu, i) => (
                                <tr key={`${stu.studentId || stu.admissionNumber || stu.studentName}-${i}`}>
                                  <td className="fw-semibold">{i + 1}</td>
                                  <td className="fw-semibold text-dark">
                                    {stu.admissionNumber || "—"}
                                  </td>
                                  <td className="fw-semibold text-dark">
                                    {stu.studentName || "N/A"}
                                  </td>
                                  {months.map((month, j) => (
                                    <td key={j}>
                                      {formatAmount(stu?.fees?.[month])}
                                    </td>
                                  ))}
                                  <td className="fw-bold">
                                    {formatAmount(stu?.totalVanFeePaid)}
                                  </td>
                                </tr>
                              ))}

                              <tr className="table-light">
                                <td className="fw-bold" colSpan={3}>
                                  TOTAL
                                </td>
                                {months.map((month, j) => (
                                  <td key={j} className="fw-bold">
                                    {formatAmount(
                                      students.reduce(
                                        (sum, stu) => sum + Number(stu?.fees?.[month] || 0),
                                        0
                                      )
                                    )}
                                  </td>
                                ))}
                                <td className="fw-bold">
                                  {formatAmount(
                                    students.reduce(
                                      (sum, stu) => sum + Number(stu?.totalVanFeePaid || 0),
                                      0
                                    )
                                  )}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr>
                              <td colSpan={months.length + 4} className="text-center text-muted py-4">
                                No students found for this class.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              );
            })
          )}
        </>
      )}
    </Container>
  );
};

export default VanFeeDetailedReport;